import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaSearch,
  FaSpinner,
  FaWrench,
  FaExpandArrowsAlt,
  FaLayerGroup,
} from "react-icons/fa";
import { publicGarageService } from "../services/apiService";
import { useLocationPreference } from "../contexts/LocationPreferenceContext";
import { useVehicle } from "../contexts/VehicleContext";
import NearbyGarageCard from "./NearbyGarageCard";
import GarageServiceIssuePicker, { GARAGE_ISSUE_OPTIONS } from "./GarageServiceIssuePicker";
import PeriodicServicePath from "./PeriodicServicePath";
import {
  filterWorkshopsWithinRadius,
  mapDiscoveryGarageToWorkshop,
} from "../utils/garageWorkshopMapper";
import { buildSplitCartSuggestions } from "../utils/garageSplitCart";
import { isValidGeoCoord, NEARBY_SHOP_RADIUS_KM } from "../utils/geo";

const SORT_OPTIONS = [
  { id: "best_match", label: "Best match" },
  { id: "distance", label: "Nearest" },
  { id: "rating", label: "Top rated" },
  { id: "price", label: "Lowest price" },
];

function parseSearchQuery(raw) {
  const q = String(raw || "").trim();
  if (!q) return { pincode: "", area: "" };
  const pin = q.replace(/\D/g, "").slice(0, 6);
  if (pin.length === 6 && /^\d{6}$/.test(pin)) {
    return { pincode: pin, area: "" };
  }
  return { pincode: "", area: q };
}

export default function GarageDiscovery() {
  const navigate = useNavigate();
  const {
    searchLocation,
    loading: locationLoading,
    searchLocationLabel,
  } = useLocationPreference();
  const { selectedVehicle } = useVehicle();
  const [locationInput, setLocationInput] = useState("");
  const [issueIds, setIssueIds] = useState([]);
  const [periodicMode, setPeriodicMode] = useState(false);
  const [sortBy, setSortBy] = useState("best_match");
  const [workshops, setWorkshops] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [expandedRadius, setExpandedRadius] = useState(false);
  const [radiusKm, setRadiusKm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const vehicleFuel = selectedVehicle?.fuelType || "";

  const selectedIssues = useMemo(
    () =>
      GARAGE_ISSUE_OPTIONS.filter(
        (opt) => opt.id && issueIds.includes(opt.id) && opt.clusterId != null
      ),
    [issueIds]
  );

  const primaryIssue = selectedIssues[0] || null;
  const serviceType = primaryIssue?.serviceType || "";
  const clusterId = primaryIssue?.clusterId ?? null;
  const multiIssue = selectedIssues.length >= 2;

  const hasLocation = isValidGeoCoord(searchLocation?.lat, searchLocation?.lng);

  const coords = useMemo(() => {
    if (!hasLocation) return null;
    return { lat: Number(searchLocation.lat), lng: Number(searchLocation.lng) };
  }, [hasLocation, searchLocation?.lat, searchLocation?.lng]);

  const fetchGarages = useCallback(
    async ({ page = 1, append = false, queryOverride } = {}) => {
      const queryText = queryOverride != null ? queryOverride : locationInput;
      const { pincode, area } = parseSearchQuery(queryText);
      const useCoords = !pincode && !area && coords;

      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setError("");

      try {
        // Multi-issue: fetch without hard single-cluster filter so client can build splits.
        const useClusterFilter = !periodicMode && !multiIssue;
        const result = await publicGarageService.discover({
          pincode,
          area,
          serviceType: useClusterFilter ? serviceType || undefined : undefined,
          clusterId: useClusterFilter ? clusterId || undefined : undefined,
          fuelType: vehicleFuel || undefined,
          discoveryMode: periodicMode ? "periodic" : undefined,
          sortBy: sortBy || undefined,
          page,
          limit: useCoords || multiIssue ? 48 : 10,
          lat: useCoords ? coords.lat : undefined,
          lng: useCoords ? coords.lng : undefined,
        });

        const mapped = (result.garages || []).map(mapDiscoveryGarageToWorkshop);
        const list = useCoords
          ? filterWorkshopsWithinRadius(mapped, NEARBY_SHOP_RADIUS_KM)
          : mapped;

        setWorkshops((prev) => (append ? [...prev, ...list] : list));
        setPagination(result.pagination || { page: 1, limit: 10, total: list.length, pages: 1 });
        setExpandedRadius(Boolean(result.expandedRadius));
        setRadiusKm(result.radiusKm ?? null);
        setHasSearched(Boolean(pincode || area || serviceType || periodicMode || issueIds.length));
      } catch (err) {
        setError(err.message || "Unable to load partner garages.");
        if (!append) setWorkshops([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [locationInput, serviceType, clusterId, periodicMode, sortBy, vehicleFuel, coords, multiIssue, issueIds.length]
  );

  useEffect(() => {
    if (locationLoading) return;
    fetchGarages({ page: 1 });
  }, [serviceType, clusterId, periodicMode, sortBy, vehicleFuel, coords, locationLoading, multiIssue, issueIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    fetchGarages({ page: 1 });
  };

  const handleIssuesChange = (ids) => {
    setPeriodicMode(false);
    setIssueIds(Array.isArray(ids) ? ids : []);
  };

  const handlePeriodicToggle = (next) => {
    setPeriodicMode(next);
    if (next) {
      setIssueIds([]);
    }
  };

  const handleLoadMore = () => {
    if (pagination.page >= pagination.pages || loadingMore) return;
    fetchGarages({ page: pagination.page + 1, append: true });
  };

  const splitSuggestions = useMemo(
    () => buildSplitCartSuggestions(workshops, selectedIssues),
    [workshops, selectedIssues]
  );

  const displayWorkshops = useMemo(() => {
    if (!multiIssue) return workshops;
    if (splitSuggestions.combined.length) return splitSuggestions.combined;
    // When showing splits only, still show nearby for browsing
    return workshops;
  }, [multiIssue, workshops, splitSuggestions.combined]);

  const showSplitBanner =
    multiIssue &&
    !periodicMode &&
    !splitSuggestions.combined.length &&
    splitSuggestions.splits.length > 0;

  const startSplitBooking = (split) => {
    navigate("/service/booking/split", {
      state: {
        split,
        issues: selectedIssues,
      },
    });
  };

  const showLoading = locationLoading || loading;

  return (
    <div className="w-full px-4 pt-3 pb-6 sm:px-6 sm:pt-4 lg:px-8">
      <div className="mx-auto w-full max-w-6xl">
        <header className="mb-5">
          <div className="flex items-center gap-2">
            <FaWrench className="text-blue-500" />
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Nearby Garages &amp; Service Centres
            </h1>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {hasLocation
              ? `Partner garages ranked by match${searchLocationLabel ? ` near ${searchLocationLabel}` : ""}.`
              : "Search by pincode or area, or enable location for nearby results."}
            {vehicleFuel ? ` · Filtering for ${vehicleFuel} vehicles.` : ""}
          </p>
        </header>

        <div className="mb-4 space-y-4">
          <PeriodicServicePath active={periodicMode} onToggle={handlePeriodicToggle} />
          {!periodicMode && (
            <GarageServiceIssuePicker value={issueIds} onChange={handleIssuesChange} />
          )}
        </div>

        <form onSubmit={handleSearch} className="mb-4">
          <label htmlFor="garage-location-search" className="sr-only">
            Pincode or area
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <FaMapMarkerAlt className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                id="garage-location-search"
                type="text"
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                placeholder="Enter pincode or area (e.g. 500081 or Madhapur)"
                className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-10 pr-4 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                autoComplete="postal-code"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-800"
              aria-label="Sort garages"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={showLoading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-3 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
            >
              {showLoading ? <FaSpinner className="animate-spin" /> : <FaSearch />}
              Search
            </button>
          </div>
        </form>

        {expandedRadius && radiusKm != null && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">
            <FaExpandArrowsAlt className="shrink-0 text-amber-600" aria-hidden />
            Expanded to {radiusKm}km
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {showSplitBanner && (
          <div className="mb-5 space-y-3">
            <div className="flex items-start gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-950">
              <FaLayerGroup className="mt-0.5 shrink-0 text-indigo-600" />
              <div>
                <p className="font-semibold">No single nearby garage covers all selected issues</p>
                <p className="mt-0.5 text-indigo-800/90">
                  Book a coordinated split: separate job cards, one checkout flow.
                </p>
              </div>
            </div>
            {splitSuggestions.splits.map((split) => (
              <div
                key={split.id}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
              >
                <p className="text-sm font-bold text-gray-900">{split.title}</p>
                <ul className="mt-3 space-y-2">
                  {split.legs.map((leg) => (
                    <li
                      key={`${leg.clusterId}-${leg.garage?.id}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm"
                    >
                      <div>
                        <span className="font-semibold text-gray-900">{leg.label}</span>
                        <span className="text-gray-500"> · {leg.garage?.name}</span>
                        {leg.garage?.distance != null && (
                          <span className="text-gray-400"> · {leg.garage.distance} km</span>
                        )}
                      </div>
                      <Link
                        to={`/garages/${leg.garage?.id}`}
                        className="text-xs font-semibold text-blue-600 hover:underline"
                      >
                        View garage
                      </Link>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => startSplitBooking(split)}
                  className="mt-4 w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 sm:w-auto sm:px-6"
                >
                  Book split services
                </button>
              </div>
            ))}
          </div>
        )}

        {multiIssue && splitSuggestions.combined.length > 0 && (
          <p className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-900">
            {splitSuggestions.combined.length} garage
            {splitSuggestions.combined.length === 1 ? "" : "s"} can handle all selected issues in one visit.
          </p>
        )}

        {showLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-500">
            <FaSpinner className="animate-spin text-2xl text-blue-500" />
            <p className="text-sm">Finding partner garages…</p>
          </div>
        ) : displayWorkshops.length === 0 && !showSplitBanner ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
            <FaWrench className="mx-auto text-3xl text-gray-300" />
            <p className="mt-3 text-base font-semibold text-gray-900">No partner garages found</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
              {periodicMode
                ? "No Medium or Large garages with periodic packages in this area. Try another location."
                : hasSearched
                  ? "Try a different pincode, area, or issue filter."
                  : hasLocation
                    ? `No garages within ${NEARBY_SHOP_RADIUS_KM} km. Search by pincode or area above.`
                    : "Enter your pincode or area above, or allow location access for nearby garages."}
            </p>
          </div>
        ) : (
          <>
            <p className="mb-3 text-xs font-medium text-gray-500">
              {displayWorkshops.length} partner garage{displayWorkshops.length === 1 ? "" : "s"} found
              {periodicMode ? " · Periodic service mode" : ""}
              {multiIssue ? " · Multi-issue search" : ""}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {displayWorkshops.map((workshop, i) => (
                <NearbyGarageCard
                  key={workshop.id || `${workshop.name}-${i}`}
                  workshop={workshop}
                  tierIndex={i}
                  index={i}
                  compact
                />
              ))}
            </div>

            {!multiIssue && pagination.page < pagination.pages && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-6 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                >
                  {loadingMore ? <FaSpinner className="animate-spin" /> : null}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
